import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { BrandMarkIcon, SunIcon, MoonIcon, MonitorIcon, ChevronRightIcon } from '@/components/Icons';
import { useLaunchState } from '@/context/LaunchContext';
import { useSessionState } from '@/context/SessionContext';
import { useThemeState } from '@/context/ThemeContext';
import { fetchIntroContent } from '@/lib/api';

type IntroContent = {
  appName: string;
  heroTitle: string;
  heroSubtitle: string;
  primaryAction: { label: string; target: string };
  secondaryAction: { label: string; target: string };
};

const fallbackIntroContent: IntroContent = {
  appName: 'SlotLooks',
  heroTitle: 'Run every visit from one simple dashboard.',
  heroSubtitle: 'Track bookings, clients, and shifts in one place.',
  primaryAction: { label: 'Get started', target: '/login' },
  secondaryAction: { label: 'Sign in', target: '/login' },
};

function ThemeCycleButton() {
  const { themeMode, setThemeMode } = useThemeState();

  const nextMode = useMemo(() => {
    switch (themeMode) {
      case 'system':
        return 'light';
      case 'light':
        return 'dark';
      case 'dark':
      default:
        return 'system';
    }
  }, [themeMode]);

  const icon = useMemo(() => {
    switch (themeMode) {
      case 'light':
        return <SunIcon size={18} />;
      case 'dark':
        return <MoonIcon size={18} />;
      case 'system':
      default:
        return <MonitorIcon size={18} />;
    }
  }, [themeMode]);

  return (
    <button
      className="icon-button"
      type="button"
      aria-label="Switch theme mode"
      title={`Switch to ${nextMode} mode`}
      onClick={() => {
        void setThemeMode(nextMode);
      }}>
      {icon}
    </button>
  );
}

export function WelcomePage() {
  const navigate = useNavigate();
  const { welcomeSeen, setWelcomeSeen } = useLaunchState();
  const { isAuthenticated, sessionRole } = useSessionState();
  const [introContent, setIntroContent] = useState<IntroContent>(fallbackIntroContent);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let active = true;

    fetchIntroContent()
      .then((content) => {
        if (!active) {
          return;
        }

        setIntroContent((current) => ({
          ...current,
          ...content,
        }));
      })
      .catch(() => {
        // Fallback copy already covers the screen.
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (welcomeSeen === null) {
      return;
    }

    if (welcomeSeen && !isAuthenticated) {
      navigate('/login', { replace: true });
    }

    if (isAuthenticated) {
      navigate(sessionRole === 'staff' ? '/attendance' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, sessionRole, welcomeSeen]);

  async function handleGetStarted() {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await setWelcomeSeen(true);
      navigate('/login', { replace: true });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="app-root welcome-root">
      <div className="welcome-atmosphere" aria-hidden="true">
        <div className="welcome-glow welcome-glow-a" />
        <div className="welcome-glow welcome-glow-b" />
        <div className="welcome-glow welcome-glow-c" />
        <div className="welcome-grid-fade" />
        <div className="welcome-visual">
          <div className="welcome-visual-mark">
            <BrandMarkIcon />
          </div>
        </div>
      </div>

      <div className="welcome-shell">
        <header className="welcome-top-nav">
          <div className="header-actions welcome-nav-actions">
            <ThemeCycleButton />
            <Link className="ghost-button welcome-nav-link" to="/login">
              Sign in
            </Link>
          </div>
        </header>

        <main className="welcome-stage">
          <section className="welcome-hero">
            <div className="welcome-copy">
              <p className="welcome-brand">{introContent.appName}</p>

              <h1 className="welcome-headline">{introContent.heroTitle}</h1>

              <p className="welcome-lede">{introContent.heroSubtitle}</p>

              <div className="welcome-actions">
                <button className="primary-button" type="button" onClick={handleGetStarted} disabled={isSaving}>
                  {isSaving ? 'Preparing...' : introContent.primaryAction.label}
                  <ChevronRightIcon size={16} />
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
